import { c as _c } from "react/compiler-runtime";
import React from "react";
import { Keyboard } from "lucide-react";
import { motion, useReducedMotion } from "motion/react";
import { Button } from "@/components/ui/button";
import { HoverCard, HoverCardContent, HoverCardTrigger } from "@/components/ui/hover-card";
import { ShortcutKeys } from "@/components/ui/shortcut-keys";
import { useIsMobile } from "@/hooks/use-mobile";
import { cn } from "@/lib/utils";

/**
 * The anvil's springy overshoot curve (`anvil-enter` in globals.css), on the
 * snappy hover beat ScrollToBottomButton uses: hover feedback tracks the
 * pointer, so any lag reads as the pill being unresponsive.
 */
export const HOVER_SPRING = {
  duration: 0.12,
  ease: [0.34, 1.56, 0.64, 1]
} as const;

/** The 36px frosted-glass circle the floating pills share (overrides the
 *  design-system pill's pinned 32px box, padding, and flat colors). */
export const FLOATING_CIRCLE_BUTTON_CLASSES = cn("!h-9 !min-h-9 !max-h-9 w-9 !p-0", "border-black/10 bg-white/70 shadow-sm backdrop-blur-md hover:border-black/10 hover:bg-white dark:border-white/15 dark:bg-neutral-900/70 dark:hover:border-white/15 dark:hover:bg-neutral-900");
export interface ShortcutsPillProps {
  /** Force the hover card open (useful for demos and visual tests). */
  open?: boolean;
  modelSwitcherEnabled?: boolean;
  className?: string;
}
interface CodesmithShortcut {
  keys: string[];
  description: string;
}

/** Defaults to Mac for SSR; corrected after mount from `navigator`. */
function useIsMac() {
  const $ = _c(2);
  const [isMac, setIsMac] = React.useState(true);
  let t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = () => {
      setIsMac(/Mac|iPhone|iPad|iPod/.test(navigator.userAgent));
    };
    t1 = [];
    $[0] = t0;
    $[1] = t1;
  } else {
    t0 = $[0];
    t1 = $[1];
  }
  React.useEffect(t0, t1);
  return isMac;
}
function getCodesmithShortcuts(isMac: boolean, modelSwitcherEnabled: boolean): CodesmithShortcut[] {
  const opt = isMac ? "⌥" : "Alt";
  const cmd = isMac ? "⌘" : "Ctrl";
  return [{
    keys: ["C"],
    description: "Open [code]smith"
  }, {
    keys: [opt, "⇧", "N"],
    description: "New chat"
  }, {
    keys: [opt, "B"],
    description: "Toggle chat list"
  }, {
    keys: [opt, "L"],
    description: "Focus chat input"
  }, {
    keys: [`${opt} ×2`],
    description: "Session switcher"
  }, {
    keys: [opt, "D"],
    description: "Archive session"
  }, {
    keys: [opt, "G"],
    description: "Open session's PRs"
  }, {
    keys: [opt, "P"],
    description: "Open PR previews"
  }, {
    keys: [cmd, "↵"],
    description: "Queue message"
  }, ...(modelSwitcherEnabled ? [{
    keys: [cmd, "⇧", "."],
    description: "Toggle Fast/Deep mode"
  }] : [])];
}
function ShortcutsPanel(t0) {
  const $ = _c(6);
  const {
    modelSwitcherEnabled
  } = t0;
  const isMac = useIsMac();
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <div className="mb-2 flex items-center gap-1.5"><Keyboard className="size-3.5 text-neutral-500 dark:text-neutral-400" /><span className="text-xs font-medium text-neutral-800 dark:text-neutral-100">[code]smith shortcuts</span></div>;
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== isMac || $[2] !== modelSwitcherEnabled) {
    t2 = getCodesmithShortcuts(isMac, modelSwitcherEnabled).map(_temp);
    $[1] = isMac;
    $[2] = modelSwitcherEnabled;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== t2) {
    t3 = <div className="w-72 rounded-xl border border-[#0c0c0d14] bg-white p-3 shadow-[0px_10px_20px_0px_rgba(0,0,0,0.12)] dark:border-white/12 dark:bg-[#2A2C2C] dark:shadow-[0px_10px_20px_0px_rgba(0,0,0,0.2)]">{t1}<div className="space-y-1.5">{t2}</div></div>;
    $[4] = t2;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  return t3;
}

/**
 * Floating glass pill anchored in the [code]smith chat pane; hovering it
 * reveals the [code]smith keyboard shortcuts.
 */
function _temp(shortcut) {
  return <div key={shortcut.description} className="flex items-center justify-between gap-x-4"><span className="text-xs text-neutral-600 dark:text-neutral-300">{shortcut.description}</span><ShortcutKeys keys={shortcut.keys} /></div>;
}
export function ShortcutsPill(t0) {
  const $ = _c(20);
  const {
    open,
    modelSwitcherEnabled: t1,
    className
  } = t0;
  const modelSwitcherEnabled = t1 === undefined ? false : t1;
  const prefersReducedMotion = useReducedMotion();
  const isMobile = useIsMobile();
  if (isMobile) {
    return null;
  }
  let t2;
  if ($[0] !== className) {
    t2 = cn(FLOATING_CIRCLE_BUTTON_CLASSES, className);
    $[0] = className;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== prefersReducedMotion) {
    t3 = prefersReducedMotion ? undefined : {
      scale: 1.1,
      rotate: -6
    };
    $[2] = prefersReducedMotion;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== prefersReducedMotion) {
    t4 = prefersReducedMotion ? undefined : {
      scale: 0.92
    };
    $[4] = prefersReducedMotion;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <Keyboard className="size-4 text-neutral-600 dark:text-neutral-300" />;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  let t6;
  if ($[7] !== t3 || $[8] !== t4) {
    t6 = <motion.button whileHover={t3} whileTap={t4} transition={HOVER_SPRING}>{t5}</motion.button>;
    $[7] = t3;
    $[8] = t4;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  let t7;
  if ($[10] !== t2 || $[11] !== t6) {
    t7 = <HoverCardTrigger asChild={true}><Button asChild={true} variant="secondary" type="button" aria-label="[code]smith keyboard shortcuts" className={t2}>{t6}</Button></HoverCardTrigger>;
    $[10] = t2;
    $[11] = t6;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  let t8;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = cn("w-auto border-none bg-transparent p-0 shadow-none dark:bg-transparent", "data-[state=open]:animate-[anvil-enter_0.2s_cubic-bezier(0.34,1.56,0.64,1)_both]", "data-[state=closed]:animate-[anvil-exit_0.15s_cubic-bezier(0.36,0,0.66,-0.56)_both]", "motion-reduce:data-[state=closed]:animate-none motion-reduce:data-[state=open]:animate-none");
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  let t9;
  if ($[14] !== modelSwitcherEnabled) {
    t9 = <HoverCardContent side="top" align="end" sideOffset={8} className={t8}><ShortcutsPanel modelSwitcherEnabled={modelSwitcherEnabled} /></HoverCardContent>;
    $[14] = modelSwitcherEnabled;
    $[15] = t9;
  } else {
    t9 = $[15];
  }
  let t10;
  if ($[16] !== open || $[17] !== t7 || $[18] !== t9) {
    t10 = <HoverCard open={open} openDelay={100} closeDelay={150}>{t7}{t9}</HoverCard>;
    $[16] = open;
    $[17] = t7;
    $[18] = t9;
    $[19] = t10;
  } else {
    t10 = $[19];
  }
  return t10;
}